import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Tag, 
  message,
  Button,
  Space
} from 'antd';
import { 
  ArrowLeftOutlined, 
  DownloadOutlined, 
  BarChartOutlined,
  TrophyOutlined,
  UserOutlined,
  RobotOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AIAssistant from './AIAssistant';

const TestStatistics: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [aiVisible, setAiVisible] = useState(false);

  // Fetch statistics
  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/statistics/${testId}`);
      setStatistics(response.data);
    } catch (error) {
      message.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (testId) {
      fetchStatistics();
    }
  }, [testId]);

  // Export data
  const handleExport = () => {
    if (!statistics) return;
    
    const csvContent = [
      ['Name', 'Class', 'Score', 'Max Score', 'Accuracy (%)', 'Completed At'],
      ...statistics.results.map((result: any) => [
        result.student_name,
        result.class_name,
        result.total_score,
        result.max_score,
        result.percentage.toFixed(1),
        new Date(result.completed_at).toLocaleString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${statistics.testId}_statistics.csv`;
    link.click();
  };

  // Export knowledge point statistics
  const handleExportKnowledgePoints = () => {
    if (!statistics || !statistics.knowledgePointStats) return;
    
    const csvContent = [
      ['Knowledge Point', 'Correct', 'Total', 'Accuracy (%)'],
      ...statistics.knowledgePointStats.map((kp: any) => [
        kp.name,
        kp.correct,
        kp.total,
        kp.accuracy.toFixed(1)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${statistics.testId}_knowledge_points.csv`;
    link.click();
  };

  const columns = [
    {
      title: 'Rank',
      key: 'rank',
      render: (_: any, __: any, index: number) => index + 1,
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'student_name',
      key: 'student_name',
    },
    {
      title: 'Class',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: 'Score',
      dataIndex: 'total_score',
      key: 'total_score',
      render: (score: number, record: any) => (
        <span>
          {score} / {record.max_score}
        </span>
      ),
    },
    {
      title: 'Accuracy',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage: number) => (
        <div>
          <Progress 
            percent={percentage} 
            size="small" 
            status={percentage >= 60 ? 'success' : 'exception'}
            style={{ width: '100px' }}
          />
          <div style={{ marginTop: '4px', fontSize: '12px' }}>
            {percentage.toFixed(1)}%
          </div>
        </div>
      ),
    },
    {
      title: 'Grade',
      key: 'grade',
      render: (_: any, record: any) => {
        const percentage = record.percentage;
        let grade = '';
        let color = '';
        
        if (percentage >= 90) {
          grade = 'Excellent';
          color = 'gold';
        } else if (percentage >= 80) {
          grade = 'Good';
          color = 'green';
        } else if (percentage >= 70) {
          grade = 'Average';
          color = 'blue';
        } else if (percentage >= 60) {
          grade = 'Pass';
          color = 'orange';
        } else {
          grade = 'Fail';
          color = 'red';
        }
        
        return <Tag color={color}>{grade}</Tag>;
      },
    },
    {
      title: 'Completed At',
      dataIndex: 'completed_at',
      key: 'completed_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>No statistics available</div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{statistics?.test?.title ? `${statistics.test.title} – Statistics` : 'Test Statistics – Smart Test Statistics System'}</title>
        <meta name="description" content={statistics?.test?.title ? `View detailed statistics and knowledge-point analysis for "${statistics.test.title}". Includes score distribution, student rankings, and accuracy breakdown.` : 'Detailed exam statistics including score distribution, student rankings, and knowledge-point accuracy breakdown.'} />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content={statistics?.test?.title ? `${statistics.test.title} – Exam Statistics` : 'Exam Statistics'} />
        <meta property="og:description" content={statistics?.test?.title ? `Detailed analytics for "${statistics.test.title}": score distribution, student rankings, knowledge-point accuracy.` : 'Detailed exam analytics and student performance statistics.'} />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1>Test Statistics</h1>
            <p>{statistics.testId}</p>
          </div>
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
            <Button 
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
            >
              Export Data
            </Button>
            {statistics?.knowledgePointStats && statistics.knowledgePointStats.length > 0 && (
              <Button 
                icon={<DownloadOutlined />}
                onClick={handleExportKnowledgePoints}
              >
                Export Knowledge Points
              </Button>
            )}
            <Button
              icon={<RobotOutlined />}
              onClick={() => setAiVisible(true)}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff', border: 'none', borderRadius: 8
              }}
            >
              AI Analysis
            </Button>
          </Space>
        </div>
      </div>

      <div className="container">
        {/* Statistics Overview */}
        <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Participants"
                value={statistics.totalStudents}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Average Score"
                value={statistics.averageScore}
                suffix="%"
                prefix={<BarChartOutlined />}
                valueStyle={{ color: '#52c41a' }}
                precision={1}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Highest Score"
                value={statistics.highestScore}
                suffix="%"
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#fa8c16' }}
                precision={1}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Lowest Score"
                value={statistics.lowestScore}
                suffix="%"
                valueStyle={{ color: '#ff4d4f' }}
                precision={1}
              />
            </Card>
          </Col>
        </Row>

        {/* Score Distribution */}
        <Card title="Score Distribution" style={{ marginBottom: '30px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', color: '#52c41a', marginBottom: '8px' }}>
                  {statistics.results.filter((r: any) => r.percentage >= 90).length}
                </div>
                <div>Excellent (≥90%)</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', color: '#1890ff', marginBottom: '8px' }}>
                  {statistics.results.filter((r: any) => r.percentage >= 80 && r.percentage < 90).length}
                </div>
                <div>Good (80-89%)</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', color: '#fa8c16', marginBottom: '8px' }}>
                  {statistics.results.filter((r: any) => r.percentage >= 70 && r.percentage < 80).length}
                </div>
                <div>Average (70-79%)</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', color: '#ff4d4f', marginBottom: '8px' }}>
                  {statistics.results.filter((r: any) => r.percentage < 70).length}
                </div>
                <div>Needs Improvement (&lt;70%)</div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Knowledge Point Statistics */}
        {statistics.knowledgePointStats && statistics.knowledgePointStats.length > 0 && (
          <Card title="Knowledge Point Statistics" style={{ marginBottom: '30px' }}>
            <Table
              columns={[
                {
                  title: 'Knowledge Point',
                  dataIndex: 'name',
                  key: 'name',
                  width: 200,
                },
                {
                  title: 'Correct',
                  dataIndex: 'correct',
                  key: 'correct',
                  width: 100,
                  align: 'center' as const,
                },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                  width: 100,
                  align: 'center' as const,
                },
                {
                  title: 'Accuracy',
                  dataIndex: 'accuracy',
                  key: 'accuracy',
                  render: (accuracy: number) => (
                    <div>
                      <Progress 
                        percent={accuracy} 
                        size="small" 
                        status={accuracy >= 60 ? 'success' : 'exception'}
                        style={{ width: '150px' }}
                      />
                      <div style={{ marginTop: '4px', fontSize: '12px' }}>
                        {accuracy.toFixed(1)}%
                      </div>
                    </div>
                  ),
                },
              ]}
              dataSource={statistics.knowledgePointStats}
              rowKey="name"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} knowledge points`,
              }}
            />
          </Card>
        )}

        {/* Detailed Score List */}
        <Card title="Detailed Scores">
          <Table
            columns={columns}
            dataSource={statistics.results}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} records`,
            }}
            loading={loading}
          />
        </Card>

        {/* AI Analysis Assistant */}
        <AIAssistant
          visible={aiVisible}
          onClose={() => setAiVisible(false)}
          title="AI Exam Analysis"
          contextData={statistics ? `
Test ID: ${statistics.testId}
Participants: ${statistics.totalStudents}
Average Score: ${statistics.averageScore?.toFixed(1)}%
Highest Score: ${statistics.highestScore?.toFixed(1)}%
Lowest Score: ${statistics.lowestScore?.toFixed(1)}%
Pass Rate (60% line): ${statistics.results ? (statistics.results.filter((r: any) => r.percentage >= 60).length / statistics.results.length * 100).toFixed(1) : 0}%

Knowledge Point Mastery:
${statistics.knowledgePointStats?.map((kp: any) => `  - ${kp.name}: Accuracy ${kp.accuracy.toFixed(1)}% (${kp.correct}/${kp.total})`).join('\n') || 'No knowledge point data'}

Student Score Distribution (Top 10):
${statistics.results?.slice(0, 10).map((r: any, i: number) => `  ${i + 1}. ${r.student_name} (${r.class_name}): ${r.total_score}/${r.max_score} pts (${r.percentage.toFixed(1)}%)`).join('\n') || 'No data'}
          `.trim() : undefined}
          initialPrompt={statistics ? 'Please analyse the overall exam results, identify the main issues, and suggest improvements.' : undefined}
        />
      </div>
    </div>
  );
};

export default TestStatistics;
